import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Mail, Loader2, Sparkles, RefreshCw, Paperclip, ExternalLink,
  Inbox as InboxIcon, Search, Eye, Download, CornerUpRight,
  FolderCheck, CheckCircle2, Gavel, Landmark, Building2, User, X, ShieldAlert, Send,
} from "lucide-react";
import { toast } from "sonner";
import { cn, ukDate } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { HeldPostList, MailAutopilotButton } from "@/pages/legal/MailAutopilot";
import { useIsPhone } from "@/hooks/useIsPhone";
import { PhonePostboxView } from "@/pages/legal/phone/PhonePostboxView";
import {
  legalMailList, legalMailGetEmail, legalMailAttachments, legalMailAttachmentBlob,
  legalMailClassify, legalMailForward, legalMailMoveDone, legalMailAssignCase,
  legalMailSearchCases, searchLoanDiskForManualLegal, manualAddLegalCase,
} from "@/lib/api";

const TYPE_TONE = {
  "Court Document":         "bg-red-100 text-red-800 border-red-300",
  "Judgment/CCJ":           "bg-rose-100 text-rose-800 border-rose-300",
  "Statutory Demand":       "bg-orange-100 text-orange-800 border-orange-300",
  "Insolvency":             "bg-purple-100 text-purple-800 border-purple-300",
  "Enforcement/Bailiff":    "bg-amber-100 text-amber-800 border-amber-300",
  "Bank/Financial":         "bg-blue-100 text-blue-800 border-blue-300",
  "HMRC":                   "bg-rose-100 text-rose-900 border-rose-300",
  "DWP / Benefits":         "bg-rose-100 text-rose-900 border-rose-300",
  "Insurance / Pensions":   "bg-blue-100 text-blue-800 border-blue-300",
  "Companies House / Regulatory": "bg-indigo-100 text-indigo-800 border-indigo-300",
  "Personal / Confidential": "bg-rose-100 text-rose-900 border-rose-300",
  "Returned Mail":          "bg-slate-100 text-slate-600 border-slate-300",
  "General Correspondence": "bg-cyan-100 text-cyan-800 border-cyan-300",
  "Junk/Marketing":         "bg-slate-100 text-slate-500 border-slate-300",
  "Other":                  "bg-slate-100 text-slate-600 border-slate-300",
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  return today
    ? d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

const fmtKb = (b) => (b ? `${Math.round(b / 1024)} KB` : "");
const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;

const caseUrl = (caseId) =>
  caseId?.startsWith("col-") ? `/collections/ageing/${caseId}` : `/legal/case/${caseId}`;

const regardingLabel = (t) =>
  t?.regarding_company || t?.regarding_person || "";

const isPreviewable = (a) => {
  const t = (a.contentType || "").toLowerCase();
  const n = (a.name || "").toLowerCase();
  return t.includes("pdf") || t.startsWith("image/") || t.startsWith("text/")
    || /\.(pdf|png|jpe?g|gif|webp|txt|csv)$/.test(n);
};

const downloadAttachment = async (messageId, a) => {
  try {
    const blob = await legalMailAttachmentBlob(messageId, a.id);
    const url = URL.createObjectURL(blob);
    const el = document.createElement("a");
    el.href = url;
    el.download = a.name || "file";
    el.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Download failed");
  }
};

function AttachmentPreviewDialog({ messageId, att, onClose }) {
  const [url, setUrl] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let obj = null;
    let alive = true;
    (async () => {
      try {
        const blob = await legalMailAttachmentBlob(messageId, att.id);
        obj = URL.createObjectURL(blob);
        if (alive) setUrl(obj);
      } catch {
        if (alive) setErr("Couldn't load the file for preview");
      }
    })();
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [messageId, att.id]);

  const t = (att.contentType || "").toLowerCase();
  const n = (att.name || "").toLowerCase();
  const isPdf = t.includes("pdf") || n.endsWith(".pdf");
  const isImg = t.startsWith("image/") || /\.(png|jpe?g|gif|webp)$/.test(n);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl grid-cols-1" aria-describedby={undefined} data-testid="mail-attachment-preview-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2 pr-6">
            <Paperclip className="w-4 h-4 text-slate-500 shrink-0" />
            <span className="truncate">{att.name}</span>
            <span className="text-[10px] text-muted-foreground font-normal shrink-0">{fmtKb(att.size)}</span>
          </DialogTitle>
        </DialogHeader>
        {err ? (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{err}</div>
        ) : !url ? (
          <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : isPdf ? (
          <iframe title="attachment-preview" src={url} className="w-full h-[72vh] border rounded bg-white" />
        ) : isImg ? (
          <div className="max-h-[72vh] overflow-auto text-center bg-slate-50 border rounded p-2">
            <img src={url} alt={att.name} className="max-w-full mx-auto" />
          </div>
        ) : (
          <div className="p-8 text-center text-sm text-muted-foreground space-y-3">
            <div>No inline preview for this file type.</div>
            <a href={url} download={att.name || "file"}>
              <Button size="sm" variant="outline"><Download className="w-3.5 h-3.5 mr-1" />Download {att.name}</Button>
            </a>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ForwardDialog({ email, onClose, onForwarded }) {
  const [to, setTo] = useState("");
  const [comment, setComment] = useState("");
  const [alsoDone, setAlsoDone] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async () => {
    const recipients = to.split(/[,;\s]+/).map(s => s.trim()).filter(s => s.includes("@"));
    if (!recipients.length) { toast.error("Enter at least one email address"); return; }
    setSending(true);
    try {
      const r = await legalMailForward(email.id, { to: recipients, comment, move_to_done: alsoDone });
      toast.success(`Forwarded to ${recipients.join(", ")}${r.moved_to_done ? " · moved to Done" : ""}`);
      onForwarded(r.moved_to_done);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" aria-describedby={undefined} data-testid="mail-forward-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <CornerUpRight className="w-4 h-4 text-blue-600" />Forward mail
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground truncate border rounded px-2 py-1.5 bg-slate-50">
            {email.subject || "(no subject)"} — attachments are forwarded automatically
          </div>
          <Input placeholder="to@simplyfunded.co.uk (comma-separate for multiple)"
            value={to} onChange={(e) => setTo(e.target.value)} data-testid="mail-forward-to-input" />
          <Textarea placeholder="Optional note to include above the forwarded mail…"
            value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
            data-testid="mail-forward-comment-input" />
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <Switch checked={alsoDone} onCheckedChange={setAlsoDone} data-testid="mail-forward-done-switch" />
            Move to Done after forwarding
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={submit} disabled={sending} data-testid="mail-forward-send-btn">
              {sending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CornerUpRight className="w-3.5 h-3.5 mr-1" />}
              Forward
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EmailPreviewDialog({ email, onClose }) {
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let alive = true;
    legalMailGetEmail(email.id)
      .then(r => { if (alive) setMsg(r.message); })
      .catch(e => { if (alive) setErr(e.response?.data?.detail || e.message); });
    return () => { alive = false; };
  }, [email.id]);
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl grid-cols-1" aria-describedby={undefined} data-testid="mail-email-preview-dialog">
        <DialogHeader>
          <DialogTitle className="text-sm flex items-center gap-2 pr-6">
            <Mail className="w-4 h-4 text-blue-700 shrink-0" />
            <span className="truncate">{email.subject || "(no subject)"}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="text-xs text-muted-foreground -mt-2">
          {email.from_name || email.from_email} · {fmtDate(email.received)}
        </div>
        {err ? (
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">{err}</div>
        ) : !msg ? (
          <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>
        ) : (
          <iframe title="mail-preview" sandbox="allow-same-origin"
            srcDoc={msg?.body?.content || "<p>(no body)</p>"}
            className="w-full h-[68vh] border rounded bg-white" data-testid="mail-email-preview-body" />
        )}
      </DialogContent>
    </Dialog>
  );
}

// Inline per-attachment case picker — lets one scanned bundle be split
// across different cases, then filed in a single "File bulk" pass.
function RowCasePicker({ attId, value, onPick, onClear, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || q.trim().length < 2) { setResults([]); return undefined; }
    const t = setTimeout(async () => {
      setBusy(true);
      try { const r = await legalMailSearchCases(q.trim()); setResults(r.matches || []); }
      catch { setResults([]); }
      finally { setBusy(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q, open]);

  if (value) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] text-blue-800 bg-blue-50 border border-blue-300 rounded-full pl-2 pr-1 py-0.5 shrink-0 max-w-[180px]"
        data-testid={`mail-att-case-chip-${attId}`}>
        <FolderCheck className="w-2.5 h-2.5 shrink-0" />
        <span className="truncate">{value.label}</span>
        <button type="button" onClick={onClear} className="text-blue-400 hover:text-blue-700" title="Clear"
          data-testid={`mail-att-case-clear-${attId}`}>
          <X className="w-3 h-3" />
        </button>
      </span>
    );
  }
  if (!open) {
    return (
      <button type="button" disabled={disabled} onClick={() => setOpen(true)}
        className="text-[10px] text-slate-500 border border-dashed rounded-full px-2 py-0.5 hover:text-blue-700 hover:border-blue-400 transition shrink-0 disabled:opacity-40"
        data-testid={`mail-att-case-btn-${attId}`}>
        <Search className="w-2.5 h-2.5 inline mr-0.5" />assign case…
      </button>
    );
  }
  return (
    <span className="relative shrink-0">
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
        onBlur={() => setTimeout(() => setOpen(false), 250)}
        placeholder="Borrower or SF#…"
        className="h-6 w-40 text-[11px] border rounded px-1.5"
        data-testid={`mail-att-case-search-${attId}`} />
      {busy && <Loader2 className="absolute right-1 top-1.5 w-3 h-3 animate-spin text-slate-400" />}
      {results.length > 0 && (
        <span className="absolute z-50 top-7 right-0 w-64 bg-white border rounded shadow-lg max-h-40 overflow-y-auto block">
          {results.map(c => (
            <button key={c.case_id} type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onPick({ case_id: c.case_id, label: c.customer_name || c.case_id }); setOpen(false); setQ(""); }}
              className="w-full text-left px-2 py-1.5 text-[11px] hover:bg-slate-50 border-b last:border-b-0 block"
              data-testid={`mail-att-case-opt-${attId}-${c.case_id}`}>
              <span className="font-medium">{c.customer_name || "—"}</span>
              {c.loan_application_id && <span className="text-muted-foreground"> · {c.loan_application_id}</span>}
            </button>
          ))}
        </span>
      )}
    </span>
  );
}

function AssignCaseDialog({ email, onClose, onAssigned, onFiled, actor }) {
  const t = email.triage;
  const [q, setQ] = useState(
    t?.matched_case?.customer_name || t?.sf_no || regardingLabel(t) || "");
  const [portal, setPortal] = useState([]);
  const [ld, setLd] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [autoDone, setAutoDone] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [atts, setAtts] = useState([]);
  const [checked, setChecked] = useState(new Set());
  const [names, setNames] = useState({});   // attId -> operator-chosen filename (forced rename)
  const [filed, setFiled] = useState({});   // attId -> case label
  const [rowCase, setRowCase] = useState({}); // attId -> { case_id, label }
  const [previewAtt, setPreviewAtt] = useState(null);
  const [mailPreview, setMailPreview] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    if (!email.has_attachments) return;
    legalMailAttachments(email.id).then(r => {
      const list = r.attachments || [];
      setAtts(list);
      setChecked(new Set(list.map(a => a.id)));
      setNames(Object.fromEntries(list.map(a => [a.id, a.name || ""])));
    }).catch(() => {});
  }, [email.id, email.has_attachments]);

  const allSelected = atts.length === 0 || checked.size === atts.length;
  // Emma #5 — filing is blocked until every file being filed has been RENAMED
  // (post scans arrive as "Scan_0001.pdf" — force a sensible OneDrive name).
  const renamed = (a) => {
    const v = (names[a.id] || "").trim();
    return !!v && v !== (a.name || "").trim();
  };
  const fileIds = allSelected
    ? atts.filter(a => !filed[a.id]).map(a => a.id)
    : [...checked];
  const unrenamed = atts.filter(a => fileIds.includes(a.id) && !renamed(a));
  const namesFor = (ids) => Object.fromEntries(ids.map(id => [id, (names[id] || "").trim()]));
  const canFile = (atts.length === 0 || checked.size > 0) && unrenamed.length === 0;

  // Per-row case assignments grouped by case, ready for one "File bulk" pass.
  const bulkGroups = useMemo(() => {
    const g = {};
    for (const [attId, c] of Object.entries(rowCase)) {
      if (filed[attId]) continue;
      if (!g[c.case_id]) g[c.case_id] = { caseInfo: c, ids: [] };
      g[c.case_id].ids.push(attId);
    }
    return Object.values(g);
  }, [rowCase, filed]);

  const bulkUnrenamed = bulkGroups
    .flatMap(g => g.ids)
    .map(id => atts.find(a => a.id === id))
    .filter(a => a && !renamed(a));

  const toggleAtt = (id) => {
    const n = new Set(checked);
    if (n.has(id)) n.delete(id); else n.add(id);
    if (atts.length > 0 && n.size < atts.length) setAutoDone(false);
    setChecked(n);
  };

  const [ldSearching, setLdSearching] = useState(false);

  const runSearch = useCallback(async (term) => {
    if (!term || term.trim().length < 2) return;
    setSearching(true);
    setLdSearching(true);
    setLd([]);
    const q = term.trim();
    // Portal results render immediately; slow LoanDisk search streams in after.
    const portalPromise = legalMailSearchCases(q).catch(() => ({ matches: [] }));
    portalPromise.then(p => {
      setPortal(p.matches || []);
      setSearched(true);
      setSearching(false);
    });
    searchLoanDiskForManualLegal(q).catch(() => ({ results: [] })).then(async (l) => {
      const p = await portalPromise;
      const portalSfs = new Set((p.matches || []).map(m => (m.loan_application_id || "").toUpperCase()).filter(Boolean));
      setLd((l.results || []).filter(r => !portalSfs.has((r.loan_application_id || "").toUpperCase())));
      setLdSearching(false);
    });
  }, []);

  useEffect(() => { runSearch(q); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const actionToast = (r) =>
    r?.action?.date ? ` · diary action added (due ${ukDate(r.action.date)})` : "";

  const attach = async (caseId, label) => {
    setBusyKey(caseId);
    try {
      const payload = { case_id: caseId, auto_done: autoDone, actor };
      if (!allSelected) payload.attachment_ids = [...checked];
      if (fileIds.length > 0) payload.attachment_names = namesFor(fileIds);
      const r = await legalMailAssignCase(email.id, payload);
      toast.success(
        `Filed to ${r.customer_name || label} — ${r.files?.length || 0} file(s) saved to OneDrive` +
        `${r.created_folder ? " (folder created)" : ""}${actionToast(r)}${r.moved_to_done ? " · moved to Done" : ""}`);
      if (autoDone && !r.moved_to_done) {
        toast.warning("Outlook didn't move the email to Done — use the Move to Done button to retry");
      }
      if (!allSelected) {
        // Multi-letter bundle: mark these as filed, keep the dialog open so
        // the next attachment can go to a different case.
        const lbl = r.customer_name || label;
        setFiled(f => {
          const n = { ...f };
          checked.forEach(id => { n[id] = lbl; });
          return n;
        });
        setRowCase(rc => {
          const n = { ...rc };
          checked.forEach(id => delete n[id]);
          return n;
        });
        setChecked(new Set());
        setBusyKey(null);
        onFiled();
      } else {
        onAssigned(r);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
      setBusyKey(null);
    }
  };

  // File every per-row assignment in one pass — one call per case, the email
  // only moves to Done when nothing is left unfiled.
  const fileBulk = async () => {
    if (bulkGroups.length === 0) return;
    setBulkBusy(true);
    const unassigned = atts.filter(a => !filed[a.id] && !rowCase[a.id]).length;
    const newlyFiled = {};
    let processed = 0;
    for (const grp of bulkGroups) {
      processed += 1;
      const isLast = processed === bulkGroups.length;
      try {
        const r = await legalMailAssignCase(email.id, {
          case_id: grp.caseInfo.case_id,
          auto_done: autoDone && isLast && unassigned === 0,
          actor,
          attachment_ids: grp.ids,
          attachment_names: namesFor(grp.ids),
        });
        const lbl = r.customer_name || grp.caseInfo.label;
        grp.ids.forEach(id => { newlyFiled[id] = lbl; });
        toast.success(
          `Filed ${grp.ids.length} file(s) to ${lbl}${r.created_folder ? " (folder created)" : ""}` +
          `${actionToast(r)}${r.moved_to_done ? " · moved to Done" : ""}`);
      } catch (e) {
        toast.error(`${grp.caseInfo.label}: ${e.response?.data?.detail || e.message}`);
      }
    }
    setFiled(f => ({ ...f, ...newlyFiled }));
    setRowCase(rc => {
      const n = { ...rc };
      Object.keys(newlyFiled).forEach(id => delete n[id]);
      return n;
    });
    setChecked(prev => {
      const n = new Set(prev);
      Object.keys(newlyFiled).forEach(id => n.delete(id));
      return n;
    });
    setBulkBusy(false);
    onFiled();
    const allFiledNow = atts.every(a => newlyFiled[a.id] || filed[a.id]);
    if (allFiledNow && unassigned === 0 && Object.keys(newlyFiled).length > 0) onClose();
  };

  const createAndAttach = async (row) => {
    setBusyKey(row.loan_application_id);
    const payload = {
      loan_id: row.loan_id,
      loan_application_id: row.loan_application_id,
      customer_name: row.borrower_name,
      balance_amount: row.balance_amount,
      principal_amount: row.principal_amount,
      days_past_due: row.days_past_due,
    };
    try {
      let r = await manualAddLegalCase(payload);
      if (r.requires_confirm) {
        if (!window.confirm(r.message || `${row.borrower_name} is already in arrears — escalating creates a Legal case. Continue?`)) {
          setBusyKey(null);
          return;
        }
        r = await manualAddLegalCase({ ...payload, force: true });
      }
      if (!r.case_id) throw new Error("Case creation didn't return a case id");
      await attach(r.case_id, row.borrower_name);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
      setBusyKey(null);
    }
  };

  const fromLabel = email.from_name || email.from_email || "";
  const receivedLabel = email.received ? fmtDate(email.received) : "";
  const toFileCount = atts.length === 0 ? 1 : fileIds.length;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl w-[96vw] p-0 gap-0 overflow-hidden grid-cols-1" aria-describedby={undefined} data-testid="mail-assign-dialog">
        <DialogHeader className="px-5 py-3.5 border-b bg-slate-50/80 space-y-1">
          <DialogTitle className="text-base flex items-center gap-2">
            <FolderCheck className="w-4 h-4 text-emerald-600" />Assign to case
            <span className="text-xs font-normal text-muted-foreground">— the email PDF + attachments are saved into the customer&apos;s OneDrive folder</span>
          </DialogTitle>
          <div className="flex items-center gap-2 text-xs text-slate-600 min-w-0">
            <span className="font-medium truncate" title={email.subject}>{email.subject || "(no subject)"}</span>
            {fromLabel && <span className="text-muted-foreground truncate">· from {fromLabel}</span>}
            {receivedLabel && <span className="text-muted-foreground shrink-0">· {receivedLabel}</span>}
            {atts.length > 0 && <span className="text-muted-foreground shrink-0">· {atts.length} attachment{atts.length === 1 ? "" : "s"}</span>}
            <button type="button" title="Preview the mail you're sorting" onClick={() => setMailPreview(true)}
              className="ml-auto shrink-0 inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-blue-700 border rounded-full px-2 py-0.5 bg-white transition"
              data-testid="mail-assign-preview-mail-btn">
              <Eye className="w-3.5 h-3.5" />Preview email
            </button>
          </div>
        </DialogHeader>

        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:divide-x max-h-[72vh]">
          {/* ── 1 · Files to save ── */}
          <section className="p-4 overflow-y-auto space-y-3" data-testid="mail-assign-files-pane">
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-bold grid place-items-center">1</span>
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">Files to save</span>
            </div>
            {atts.length === 0 ? (
              <div className="border border-dashed rounded-lg p-4 text-xs text-muted-foreground bg-slate-50/60" data-testid="mail-assign-no-attachments">
                No attachments — the covering email is saved as a PDF.
              </div>
            ) : (
              <div className="border rounded-lg p-3 space-y-2 bg-slate-50/50" data-testid="mail-assign-att-picker">
                <div className="text-[11px] text-slate-600">
                  {atts.length > 1
                    ? "Untick to split the bundle across cases, or assign a case per file below."
                    : "The attachment below will be filed with the email."}
                </div>
                <div className="text-[11px] text-amber-800 font-medium bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="mail-assign-rename-hint">
                  Rename each file before filing (required) — it&apos;s saved to OneDrive with your name plus the received date.
                </div>
                <div className="space-y-1.5">
                  {atts.map(a => (
                    <div key={a.id} className={cn("flex items-center gap-2 text-xs bg-white border rounded-md px-2 py-1.5", filed[a.id] && "opacity-60")}>
                      <Checkbox checked={checked.has(a.id)} disabled={!!filed[a.id]}
                        onCheckedChange={() => toggleAtt(a.id)}
                        data-testid={`mail-assign-att-check-${a.id}`} />
                      {filed[a.id] ? (
                        <span className="truncate flex-1 min-w-0 line-through text-slate-400">{a.name}</span>
                      ) : (
                        <Input
                          value={names[a.id] ?? ""}
                          onChange={(e) => setNames(n => ({ ...n, [a.id]: e.target.value }))}
                          className={cn("h-8 text-xs flex-1 min-w-0 bg-white",
                            !renamed(a) && "border-amber-400 focus-visible:ring-amber-400")}
                          title={renamed(a) ? `Original: ${a.name}` : "Rename this file before filing — required"}
                          data-testid={`mail-assign-att-name-${a.id}`}
                        />
                      )}
                      <span className="text-[10px] text-muted-foreground shrink-0 w-12 text-right">{fmtKb(a.size)}</span>
                      {isPreviewable(a) ? (
                        <button type="button" title="Preview" onClick={() => setPreviewAtt(a)}
                          className="text-slate-400 hover:text-blue-600 transition shrink-0"
                          data-testid={`mail-assign-att-preview-${a.id}`}>
                          <Eye className="w-4 h-4" />
                        </button>
                      ) : (
                        <button type="button" title="Download" onClick={() => downloadAttachment(email.id, a)}
                          className="text-slate-400 hover:text-blue-600 transition shrink-0"
                          data-testid={`mail-assign-att-download-${a.id}`}>
                          <Download className="w-4 h-4" />
                        </button>
                      )}
                      {filed[a.id] ? (
                        <span className="text-[10px] text-emerald-700 shrink-0 inline-flex items-center gap-0.5">
                          <FolderCheck className="w-3 h-3" />filed → {filed[a.id]}
                        </span>
                      ) : (
                        <RowCasePicker attId={a.id} value={rowCase[a.id]}
                          onPick={(c) => setRowCase(rc => ({ ...rc, [a.id]: c }))}
                          onClear={() => setRowCase(rc => { const n = { ...rc }; delete n[a.id]; return n; })} />
                      )}
                    </div>
                  ))}
                </div>
                {bulkGroups.length > 0 && (
                  <div className="flex items-center justify-between gap-2 pt-2 border-t" data-testid="mail-assign-bulk-bar">
                    <span className="text-[11px] text-blue-800 font-medium">
                      {bulkGroups.reduce((n, g) => n + g.ids.length, 0)} file(s) assigned across {bulkGroups.length} case(s)
                    </span>
                    <Button size="sm" className="h-8 text-xs bg-blue-700 hover:bg-blue-800"
                      disabled={bulkBusy || !!busyKey || bulkUnrenamed.length > 0}
                      title={bulkUnrenamed.length > 0 ? "Rename the highlighted file(s) before filing" : "Files each assigned attachment to its case"}
                      onClick={fileBulk} data-testid="mail-assign-file-bulk-btn">
                      {bulkBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FolderCheck className="w-3.5 h-3.5 mr-1" />}
                      File bulk
                    </Button>
                  </div>
                )}
                {!allSelected && bulkGroups.length === 0 && (
                  <div className="text-[11px] text-amber-800">
                    Only the ticked attachment(s) will be filed — the covering-email PDF is skipped.
                  </div>
                )}
              </div>
            )}
          </section>

          {/* ── 2 · Pick the customer ── */}
          <section className="p-4 overflow-y-auto space-y-3" data-testid="mail-assign-case-pane">
            <div className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-bold grid place-items-center">2</span>
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">Pick the customer</span>
            </div>
            <div className="flex gap-2">
              <Input placeholder="Search borrower, guarantor or SF# …" value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runSearch(q)}
                className="h-10 text-sm"
                data-testid="mail-assign-search-input" />
              <Button size="sm" className="h-10 px-3" onClick={() => runSearch(q)} disabled={searching}
                data-testid="mail-assign-search-btn">
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </Button>
            </div>

            <div className="space-y-3">
              {portal.length > 0 && (
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1">
                    <Gavel className="w-3 h-3" />Portal cases
                  </div>
                  <div className="space-y-1.5" data-testid="mail-assign-portal-results">
                    {portal.map(c => (
                      <div key={c.case_id} className="flex items-center gap-3 border rounded-lg px-3 py-2.5 bg-white hover:border-slate-400 transition-colors">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{c.customer_name || "—"}</div>
                          <div className="text-[11px] text-muted-foreground flex items-center gap-2">
                            {c.loan_application_id && <span className="font-mono">{c.loan_application_id}</span>}
                            <Badge variant="outline" className="text-[9px] px-1 py-0 capitalize">
                              {(c.stage || c.source || "").replace(/_/g, " ")}
                            </Badge>
                            {c.days_past_due ? <span>{c.days_past_due} DPD</span> : null}
                          </div>
                        </div>
                        <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700"
                          disabled={!!busyKey || !canFile}
                          title={unrenamed.length > 0
                            ? "Rename the highlighted attachment(s) before filing — required for sensible file names"
                            : canFile ? "Files the ticked attachment(s) to this case now" : "Tick at least one attachment to file"}
                          onClick={() => attach(c.case_id, c.customer_name)}
                          data-testid={`mail-assign-attach-${c.case_id}`}>
                          {busyKey === c.case_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><FolderCheck className="w-3.5 h-3.5 mr-1" />File here</>}
                        </Button>
                        {atts.length > 1 && (
                          <Button size="sm" variant="outline" className="h-8 text-[11px] text-blue-700 border-blue-300 hover:bg-blue-50"
                            disabled={!!busyKey || bulkBusy}
                            title="Assign this case to every remaining attachment row — then press File bulk"
                            onClick={() => setRowCase(rc => {
                              const n = { ...rc };
                              atts.forEach(a => { if (!filed[a.id]) n[a.id] = { case_id: c.case_id, label: c.customer_name || c.case_id }; });
                              return n;
                            })}
                            data-testid={`mail-assign-apply-all-${c.case_id}`}>
                            Apply to all
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {ld.length > 0 && (
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5 flex items-center gap-1">
                    <Landmark className="w-3 h-3" />LoanDisk (not in portal yet)
                  </div>
                  <div className="space-y-1.5" data-testid="mail-assign-loandisk-results">
                    {ld.map(r => (
                      <div key={`${r.loan_id}-${r.loan_application_id}`} className="flex items-center gap-3 border border-dashed rounded-lg px-3 py-2.5 bg-amber-50/40">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{r.borrower_name || "—"}</div>
                          <div className="text-[11px] text-muted-foreground flex items-center gap-2">
                            <span className="font-mono">{r.loan_application_id}</span>
                            <Badge variant="outline" className="text-[9px] px-1 py-0">{r.loan_status_label}</Badge>
                            <span>{gbp(r.balance_amount)} bal</span>
                            {r.days_past_due ? <span>{r.days_past_due} DPD</span> : null}
                          </div>
                        </div>
                        <Button size="sm" variant="outline" className="h-8 text-xs text-amber-800 border-amber-400 hover:bg-amber-100"
                          disabled={!!busyKey || !canFile}
                          title="Creates a legal case for this loan, then files the mail to it"
                          onClick={() => createAndAttach(r)}
                          data-testid={`mail-assign-create-${r.loan_application_id}`}>
                          {busyKey === r.loan_application_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Create case & file"}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {ldSearching && searched && (
                <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 px-1" data-testid="mail-assign-ld-loading">
                  <Loader2 className="w-3 h-3 animate-spin" />Searching LoanDisk…
                </div>
              )}

              {searched && !searching && !ldSearching && portal.length === 0 && ld.length === 0 && (
                <div className="text-center text-sm text-muted-foreground py-8 border border-dashed rounded-lg" data-testid="mail-assign-no-results">
                  No matches for &quot;{q}&quot; — try the SF number or part of the company name.
                </div>
              )}
            </div>
          </section>
        </div>

        <div className="px-5 py-3 border-t bg-white flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <Switch checked={autoDone} onCheckedChange={setAutoDone} data-testid="mail-assign-done-switch" />
            Move to Done after filing
          </label>
          <span className="text-[11px] text-muted-foreground" data-testid="mail-assign-footer-summary">
            {toFileCount} item{toFileCount === 1 ? "" : "s"} will be saved to OneDrive{unrenamed.length > 0 ? ` · ${unrenamed.length} still to rename` : ""}
          </span>
        </div>
        {previewAtt && (
          <AttachmentPreviewDialog messageId={email.id} att={previewAtt} onClose={() => setPreviewAtt(null)} />
        )}
        {mailPreview && (
          <EmailPreviewDialog email={email} onClose={() => setMailPreview(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function TriageCard({ email, onQuickAttach, busy }) {
  const t = email.triage;
  if (!t) return null;
  const mc = t.matched_case;
  return (
    <div className="mx-4 mt-3 border rounded-lg bg-violet-50/50 border-violet-200 p-3 space-y-1.5" data-testid="mail-triage-card">
      <div className="flex items-center gap-2 flex-wrap">
        <Sparkles className="w-3.5 h-3.5 text-violet-600 shrink-0" />
        <Badge variant="outline" className={cn("text-[10px]", TYPE_TONE[t.letter_type] || TYPE_TONE.Other)}>
          {t.letter_type}
        </Badge>
        {t.confidence ? <span className="text-[10px] text-muted-foreground">{t.confidence}%</span> : null}
        {t.sender_org && (
          <span className="text-[11px] text-slate-600 inline-flex items-center gap-1">
            <Landmark className="w-3 h-3 text-slate-400" />{t.sender_org}
          </span>
        )}
      </div>
      {(t.regarding_company || t.regarding_person) && (
        <div className="text-xs text-slate-700 flex items-center gap-3 flex-wrap">
          {t.regarding_company && (
            <span className="inline-flex items-center gap-1 font-medium">
              <Building2 className="w-3 h-3 text-slate-400" />{t.regarding_company}
            </span>
          )}
          {t.regarding_person && (
            <span className="inline-flex items-center gap-1">
              <User className="w-3 h-3 text-slate-400" />{t.regarding_person}
            </span>
          )}
          {t.sf_no && <Badge variant="outline" className="text-[9px] px-1 py-0">{t.sf_no}</Badge>}
        </div>
      )}
      {t.summary && <div className="text-xs text-slate-600">{t.summary}</div>}
      {t.action_required && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          ⚠ {t.action_required}
        </div>
      )}
      {mc?.case_id && !email.assigned && (
        <div className="flex items-center gap-2 pt-0.5">
          <span className="text-[11px] text-emerald-700">Suggested case: <b>{mc.customer_name}</b>{mc.loan_application_id ? ` (${mc.loan_application_id})` : ""}</span>
          <Button size="sm" className="h-6 text-[10px] px-2 bg-emerald-600 hover:bg-emerald-700"
            disabled={busy} onClick={() => onQuickAttach(mc)}
            data-testid="mail-quick-attach-btn">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><FolderCheck className="w-3 h-3 mr-1" />File to this case</>}
          </Button>
        </div>
      )}
    </div>
  );
}

function ReadingPane({ email, tab, actor, onChanged, onOpenAssign, onOpenForward }) {
  const navigate = useNavigate();
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [atts, setAtts] = useState([]);
  const [previewAtt, setPreviewAtt] = useState(null);
  const [classifying, setClassifying] = useState(false);
  const [quickBusy, setQuickBusy] = useState(false);
  const [movingDone, setMovingDone] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setMsg(null);
    setAtts([]);
    (async () => {
      try {
        const [m, a] = await Promise.all([
          legalMailGetEmail(email.id),
          email.has_attachments ? legalMailAttachments(email.id) : Promise.resolve({ attachments: [] }),
        ]);
        if (!alive) return;
        setMsg(m.message);
        setAtts(a.attachments || []);
      } catch (e) {
        if (alive) toast.error(e.response?.data?.detail || e.message);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [email.id, email.has_attachments]);

  const classify = async () => {
    setClassifying(true);
    try {
      let r = await legalMailClassify(email.id, !!email.triage);
      if (r.status === "processing") {
        toast.info("Reading the scan — OCR of a post bundle can take a couple of minutes…");
        for (let i = 0; i < 30; i += 1) {
          await new Promise(res => setTimeout(res, 8000));
          r = await legalMailClassify(email.id, false);
          if (r.status === "done") break;
        }
      }
      if (r.status === "done") {
        toast.success("Letter triaged");
        onChanged();
      } else {
        toast.error("Triage is taking longer than expected — the badge will appear once it finishes.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setClassifying(false); }
  };

  const quickAttach = async (mc) => {
    setQuickBusy(true);
    try {
      const r = await legalMailAssignCase(email.id, { case_id: mc.case_id, auto_done: true, actor });
      toast.success(`Filed to ${r.customer_name || mc.customer_name} — ${r.files?.length || 0} file(s) saved${r?.action?.date ? ` · diary action added (due ${ukDate(r.action.date)})` : ""}${r.moved_to_done ? " · moved to Done" : ""}`);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setQuickBusy(false); }
  };

  const moveDone = async () => {
    setMovingDone(true);
    try {
      await legalMailMoveDone(email.id);
      toast.success("Moved to Done");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setMovingDone(false); }
  };

  return (
    <>
      <div className="px-4 pt-3 pb-2 border-b space-y-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate" data-testid="mail-reading-subject">{email.subject || "(no subject)"}</div>
            <div className="text-xs text-muted-foreground truncate">
              {email.from_name || email.from_email} · {fmtDate(email.received)}
              {email.web_link && (
                <a href={email.web_link} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center text-blue-600 hover:underline">
                  <ExternalLink className="w-3 h-3 mr-0.5" />Outlook
                </a>
              )}
            </div>
          </div>
        </div>
        {(email.assigned || []).length > 0 && (
          <div className="flex flex-wrap gap-1" data-testid="mail-assigned-chips">
            {email.assigned.map(a => (
              <button type="button" key={a.case_id}
                className="inline-flex items-center gap-1 min-w-0 max-w-[240px] text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-300 rounded-full px-2 py-0.5 hover:bg-emerald-100"
                onClick={() => navigate(caseUrl(a.case_id))}
                title={`Filed to ${a.customer_name || a.case_id} — open case`}
                data-testid={`mail-assigned-chip-${a.case_id}`}>
                <FolderCheck className="w-3 h-3 shrink-0" />
                <span className="truncate">{a.customer_name || a.case_id}</span>
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-1.5 flex-wrap">
          <Button size="sm" variant="outline" className="h-7 text-[11px] text-emerald-700 border-emerald-300 hover:bg-emerald-50"
            onClick={onOpenAssign} data-testid="mail-assign-btn">
            <FolderCheck className="w-3 h-3 mr-1" />Assign to case
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-[11px]"
            onClick={onOpenForward} data-testid="mail-forward-btn">
            <CornerUpRight className="w-3 h-3 mr-1" />Forward
          </Button>
          {tab !== "done" && (
            <Button size="sm" variant="outline" className="h-7 text-[11px] text-slate-600"
              onClick={moveDone} disabled={movingDone} data-testid="mail-move-done-btn">
              {movingDone ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
              Move to Done
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-7 text-[11px] text-slate-500"
            onClick={classify} disabled={classifying} data-testid="mail-classify-btn">
            {classifying ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1 text-violet-600" />}
            {email.triage ? "Re-triage" : "AI triage"}
          </Button>
        </div>
      </div>

      <TriageCard email={email} onQuickAttach={quickAttach} busy={quickBusy} />

      {atts.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-4 py-2 border-b" data-testid="mail-reading-attachments">
          {atts.map(a => (
            <span key={a.id}
              className="inline-flex items-center gap-1.5 border rounded-full pl-2 pr-1.5 py-0.5 text-[10px] bg-white"
              title={a.name}>
              <Paperclip className="w-2.5 h-2.5 text-slate-400" />
              <span className="max-w-[180px] truncate">{a.name}</span>
              <span className="text-muted-foreground">{fmtKb(a.size)}</span>
              {isPreviewable(a) ? (
                <button type="button" title="Preview"
                  onClick={() => setPreviewAtt(a)}
                  className="text-slate-400 hover:text-blue-600 transition shrink-0"
                  data-testid={`mail-att-preview-${a.id}`}>
                  <Eye className="w-3.5 h-3.5" />
                </button>
              ) : (
                <button type="button" title="Download"
                  onClick={() => downloadAttachment(email.id, a)}
                  className="text-slate-400 hover:text-blue-600 transition shrink-0"
                  data-testid={`mail-att-download-${a.id}`}>
                  <Download className="w-3.5 h-3.5" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      <CardContent className="p-0">
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        ) : (
          <iframe
            title="mail-body"
            sandbox="allow-same-origin"
            srcDoc={msg?.body?.content || "<p>(no body)</p>"}
            className="w-full h-[calc(100vh-440px)] bg-white"
            data-testid="mail-reading-body"
          />
        )}
      </CardContent>
      {previewAtt && (
        <AttachmentPreviewDialog messageId={email.id} att={previewAtt} onClose={() => setPreviewAtt(null)} />
      )}
    </>
  );
}

export default function MailInbox() {
  const { user, isAdmin } = useAuth();
  const admin = typeof isAdmin === "function" ? isAdmin() : false;
  const actor = user?.name || "Ops";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("inbox");
  const [selectedId, setSelectedId] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  const [forwardFor, setForwardFor] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const tabRef = useRef(tab);
  tabRef.current = tab;
  const isPhone = useIsPhone();

  const fetchEmails = useCallback(async (silent = false, folder = tabRef.current) => {
    if (folder === "held") { setLoading(false); return; }
    try {
      if (!silent) setLoading(true);
      const r = await legalMailList({ limit: 60, folder });
      setData(r);
      setErr(null);
      setLastSync(new Date());
    } catch (e) {
      if (!silent) setErr(e.response?.data?.detail || e.message);
    } finally { if (!silent) setLoading(false); }
  }, []);

  useEffect(() => { fetchEmails(false, tab); setSelectedId(null); }, [tab, fetchEmails]);
  useEffect(() => {
    const t = setInterval(() => {
      if (document.visibilityState === "visible") fetchEmails(true);
    }, 45000);
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchEmails(true);
    };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(t);
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [fetchEmails]);

  const emails = data?.emails || [];
  const selected = emails.find(e => e.id === selectedId) || null;
  const untriaged = emails.filter(e => !e.triage).length;

  const afterAction = () => { fetchEmails(true); };

  const readingPaneNode = selected ? (
    <ReadingPane
      key={selected.id}
      email={selected}
      tab={tab}
      actor={actor}
      onChanged={afterAction}
      onOpenAssign={() => setAssignFor(selected)}
      onOpenForward={() => setForwardFor(selected)}
    />
  ) : null;

  const dialogs = (
    <>
      {assignFor && (
        <AssignCaseDialog
          email={assignFor}
          actor={actor}
          onClose={() => setAssignFor(null)}
          onAssigned={() => { setAssignFor(null); afterAction(); }}
          onFiled={afterAction}
        />
      )}
      {forwardFor && (
        <ForwardDialog
          email={forwardFor}
          onClose={() => setForwardFor(null)}
          onForwarded={() => { setForwardFor(null); afterAction(); }}
        />
      )}
    </>
  );

  if (isPhone) {
    return (
      <div className="p-3" data-testid="legal-mail-inbox-page">
        <PhonePostboxView ctx={{
          data, loading, err, tab, setTab, admin, emails, untriaged, selected, setSelectedId, lastSync,
          refresh: () => fetchEmails(),
          heldList: <HeldPostList onChanged={() => fetchEmails(true, "inbox")} />,
          autopilotButton: <MailAutopilotButton onChanged={() => fetchEmails(true)} />,
          openAssign: (e) => setAssignFor(e), openForward: (e) => setForwardFor(e),
          readingPane: readingPaneNode, typeTone: TYPE_TONE, regardingLabel,
        }} />
        {dialogs}
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-4" data-testid="legal-mail-inbox-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-700" />Postbox
            <Badge variant="outline" className="text-[10px] font-normal text-muted-foreground">
              {data?.mailbox || "scanned post"}
            </Badge>
            <span className="text-[10px] font-normal text-muted-foreground">scanned post</span>
          </h1>
          {lastSync && (
            <p className="text-xs text-muted-foreground mt-0.5">
              Updated {lastSync.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border overflow-hidden">
            <button type="button"
              className={cn("px-3 py-1.5 text-xs font-medium flex items-center gap-1.5",
                tab === "inbox" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50")}
              onClick={() => setTab("inbox")} data-testid="mail-tab-inbox">
              <InboxIcon className="w-3.5 h-3.5" />Inbox
            </button>
            <button type="button"
              className={cn("px-3 py-1.5 text-xs font-medium flex items-center gap-1.5",
                tab === "done" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50")}
              onClick={() => setTab("done")} data-testid="mail-tab-done">
              <CheckCircle2 className="w-3.5 h-3.5" />Done
            </button>
            <button type="button"
              className={cn("px-3 py-1.5 text-xs font-medium flex items-center gap-1.5",
                tab === "sent" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50")}
              onClick={() => setTab("sent")} data-testid="mail-tab-sent" title="Everything sent from this mailbox">
              <Send className="w-3.5 h-3.5" />Sent
            </button>
            {admin && (
              <button type="button"
                className={cn("px-3 py-1.5 text-xs font-medium flex items-center gap-1.5",
                  tab === "held" ? "bg-amber-600 text-white" : "bg-white text-amber-800 hover:bg-amber-50")}
                onClick={() => setTab("held")} data-testid="mail-tab-held" title="Scans the privacy autopilot is holding for you">
                <ShieldAlert className="w-3.5 h-3.5" />Held
              </button>
            )}
          </div>
          {admin && <MailAutopilotButton onChanged={() => fetchEmails(true)} />}
          <Button size="sm" variant="outline" className="h-8" onClick={() => fetchEmails()}
            disabled={loading} data-testid="mail-refresh-btn">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            <span className="ml-1.5 text-xs">Pull mail</span>
          </Button>
        </div>
      </div>

      {err && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3" data-testid="mail-error">
          {String(err)}
        </div>
      )}

      <div className="grid grid-cols-12 gap-4">
        <Card className="col-span-12 lg:col-span-5 overflow-hidden">
          <div className="px-3 py-2 border-b bg-slate-50 flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-600">
              {emails.length} item{emails.length === 1 ? "" : "s"}
            </span>
            {tab === "inbox" && untriaged > 0 && (
              <span className="text-[10px] text-violet-700 inline-flex items-center gap-1">
                <Sparkles className="w-3 h-3" />{untriaged} awaiting AI triage
              </span>
            )}
          </div>
          <div className="max-h-[calc(100vh-260px)] overflow-y-auto divide-y" data-testid="mail-list">
            {tab === "held" ? (
              <HeldPostList onChanged={() => fetchEmails(true, "inbox")} />
            ) : loading && emails.length === 0 ? (
              <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
            ) : emails.length === 0 ? (
              <div className="p-10 text-center text-sm text-muted-foreground" data-testid="mail-list-empty">
                {tab === "inbox" ? "No post in the inbox — all caught up." : "Nothing in Done yet."}
              </div>
            ) : emails.map(e => {
              const t = e.triage;
              const reg = regardingLabel(t);
              return (
                <button type="button" key={e.id}
                  className={cn("w-full text-left px-3 py-2.5 hover:bg-slate-50 transition",
                    selectedId === e.id && "bg-blue-50/70 hover:bg-blue-50/70",
                    !e.is_read && "border-l-2 border-l-blue-500")}
                  onClick={() => setSelectedId(e.id)}
                  data-testid={`mail-row-${e.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className={cn("text-xs truncate", !e.is_read ? "font-semibold" : "font-medium text-slate-700")}>
                      {e.from_name || e.from_email || "—"}
                    </span>
                    <span className="text-[10px] text-muted-foreground shrink-0 flex items-center gap-1">
                      {e.has_attachments && <Paperclip className="w-3 h-3" />}
                      {fmtDate(e.received)}
                    </span>
                  </div>
                  <div className="text-xs text-slate-600 truncate mt-0.5">{e.subject || "(no subject)"}</div>
                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                    {t ? (
                      <>
                        <Badge variant="outline" className={cn("text-[9px] px-1 py-0", TYPE_TONE[t.letter_type] || TYPE_TONE.Other)}>
                          {t.letter_type}
                        </Badge>
                        {reg && <span className="text-[10px] text-slate-500 truncate max-w-[160px]">{reg}</span>}
                      </>
                    ) : (
                      <span className="text-[10px] text-slate-400 italic">awaiting triage…</span>
                    )}
                    {(e.assigned || []).map(a => (
                      <span key={a.case_id} className="inline-flex items-center gap-0.5 min-w-0 max-w-[150px] text-[9px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0">
                        <FolderCheck className="w-2.5 h-2.5 shrink-0" /><span className="truncate">{a.customer_name || "filed"}</span>
                      </span>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        <Card className="col-span-12 lg:col-span-7 overflow-hidden">
          {readingPaneNode || (
            <div className="h-[420px] flex flex-col items-center justify-center text-muted-foreground gap-2" data-testid="mail-no-selection">
              <Mail className="w-8 h-8 text-slate-300" />
              <span className="text-sm">Select a letter to read it</span>
            </div>
          )}
        </Card>
      </div>

      {dialogs}
    </div>
  );
}
